import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiService, type Classroom } from "@/services/api.service";
import {
  FlagStoreError,
  flagStore,
  type FlagDetail,
  type FlagOverride,
  type FlagScope,
  type StoreFlagDefinition,
} from "@/services/flag-store.service";
import { KIND_LABELS, SCOPE_LABELS, SCOPE_ORDER, filterFlags, findOverride, kindCounts, type KindFilter, formatStoredValue, isValidValue, overrideCount, parseDraft } from "@/lib/flag-store";
import { ConfirmChangeDialog, HistoryPanel, type PendingChange } from "./FlagStorePanels";
import { TargetSearch } from "./TargetSearch";
import { FieldSelect } from "./FieldSelect";
import { FlagListSkeleton, TableSkeleton } from "./FlagStoreSkeletons";

type Organization = { id: string; name: string };

export function FlagStoreAdmin() {
  const [flags, setFlags] = useState<StoreFlagDefinition[]>([]);
  const [loadingFlags, setLoadingFlags] = useState(true);
  const [environment, setEnvironment] = useState("");
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<KindFilter>("all");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);

  useEffect(() => {
    let active = true;
    flagStore.definitions()
      .then(result => { if (active) { setFlags(result.flags); setEnvironment(result.environment); } })
      .catch(caught => { if (active) setError(caught instanceof Error ? caught.message : "Could not load flags."); })
      .finally(() => { if (active) setLoadingFlags(false); });
    Promise.all([apiService.getOrganizations(), apiService.getClassrooms()])
      .then(([orgs, rooms]) => { if (active) { setOrganizations(orgs); setClassrooms(rooms); } })
      .catch(() => { if (active) toast.error("Organization and classroom lists could not be loaded"); });
    return () => { active = false; };
  }, []);

  const visible = useMemo(() => filterFlags(flags, kind, search), [flags, kind, search]);
  const counts = useMemo(() => kindCounts(flags), [flags]);
  const selected = flags.find(flag => flag.key === selectedKey) ?? null;

  if (error) return <Card className="p-4"><p role="alert" className="text-sm text-destructive">{error}</p></Card>;
  return <div className="flex h-full min-h-0 flex-col gap-4 overflow-auto lg:flex-row lg:overflow-hidden">
    <Card className="flex max-h-72 w-full shrink-0 flex-col gap-2 overflow-hidden p-3 lg:max-h-none lg:w-80">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold">Flags</h2>
        {environment && <Badge variant={environment === "prod" ? "destructive" : "outline"}>{environment.toUpperCase()}</Badge>}
      </div>
      {loadingFlags ? <FlagListSkeleton /> : <>
      <div role="group" aria-label="Filter flags by kind" className="flex flex-wrap gap-1">
        {counts.map(([value, count]) => <button key={value} type="button" aria-pressed={kind === value} onClick={() => setKind(value)}
          className={`rounded-full border px-2.5 py-0.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${kind === value ? "border-foreground bg-foreground text-background" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
          {KIND_LABELS[value]} <span className="tabular-nums">{count}</span>
        </button>)}
      </div>
      <Input aria-label="Search flags" placeholder="Search flags" value={search} onChange={event => setSearch(event.target.value)} />
      <ul className="min-h-0 flex-1 space-y-1 overflow-auto">
        {visible.map(flag => <li key={flag.key}>
          <button type="button" aria-current={flag.key === selectedKey} onClick={() => setSelectedKey(flag.key)}
            className={`w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${flag.key === selectedKey ? "bg-accent" : ""}`}>
            <span className="block font-mono text-xs">{flag.key}</span>
            <span className="text-xs text-muted-foreground">{flag.kind}{flag.ui_only ? " · UI only" : ""}</span>
          </button>
        </li>)}
        {flags.length > 0 && visible.length === 0 && <li className="px-2 text-sm text-muted-foreground">No flags match.</li>}
      </ul>
      </>}
    </Card>
    <div className="min-w-0 flex-1 lg:overflow-auto">
      {selected
        ? <FlagDetailView key={selected.key} definition={selected} environment={environment} organizations={organizations} classrooms={classrooms} />
        : <Card className="p-6 text-sm text-muted-foreground">Choose a flag to see where it's set and change it.</Card>}
    </div>
  </div>;
}

function FlagDetailView({ definition, environment, organizations, classrooms }: {
  definition: StoreFlagDefinition;
  environment: string;
  organizations: Organization[];
  classrooms: Classroom[];
}) {
  const [detail, setDetail] = useState<FlagDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [scope, setScope] = useState<FlagScope>(definition.scopes.includes("user") ? "user" : definition.scopes[0]!);
  const [target, setTarget] = useState("");
  const [targetLabel, setTargetLabel] = useState("");
  const [draft, setDraft] = useState("");
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setError("");
    setRefresh(value => value + 1);
  }, []);
  useEffect(() => {
    let active = true;
    flagStore.detailAll(definition.key)
      .then(result => { if (active) setDetail(result); })
      .catch(caught => { if (active) setError(caught instanceof Error ? caught.message : "Could not load overrides."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [definition.key, refresh]);

  const orgName = (id: string | null) => organizations.find(org => org.id === id)?.name;
  const classroomName = (id: string | null) => classrooms.find(room => String(room.id) === id)?.name;
  const label = (scopeValue: FlagScope, id: string | null, name?: string | null) => {
    if (scopeValue === "global") return "everyone";
    const known = name || (scopeValue === "org" ? orgName(id) : scopeValue === "classroom" ? classroomName(id) : null);
    return `${SCOPE_LABELS[scopeValue].toLowerCase()} ${known ? `${known} (${id})` : id}`;
  };
  const targetName = (scopeValue: FlagScope, id: string | null) => label(scopeValue, id);

  const targetId = scope === "global" ? null : target.trim() || null;
  const targetValid = scope === "global" || (scope === "org" ? !!targetId : !!targetId && /^[1-9][0-9]*$/.test(targetId));
  const parsed = parseDraft(definition, draft);
  const valueValid = isValidValue(definition, parsed);
  const reasonValid = reason.trim().length >= 3;
  const current = findOverride(detail, scope, targetId);

  function review(next: "set" | "clear") {
    if (!targetValid || !reasonValid || (next === "set" && !valueValid)) return;
    if (next === "clear" && !current) return;
    setPending({
      flagKey: definition.key,
      scope,
      scopeId: targetId,
      targetLabel: label(scope, targetId, current?.name),
      current: current?.value ?? null,
      next: next === "set" ? (parsed as FlagOverride["value"]) : null,
      expectedVersion: current?.version ?? null,
      reason: reason.trim(),
    });
  }

  function edit(item: FlagOverride) {
    setScope(item.scope);
    setTarget(item.scope_id ?? "");
    setTargetLabel("");
    setDraft(String(item.value));
  }

  async function confirm() {
    if (!pending) return;
    setSaving(true);
    try {
      if (pending.next === null) {
        await flagStore.clear(pending.flagKey, pending.scope, pending.scopeId, { expected_version: pending.expectedVersion ?? 0, reason: pending.reason }, environment);
      } else {
        await flagStore.set(pending.flagKey, pending.scope, pending.scopeId, { value: pending.next, expected_version: pending.expectedVersion, reason: pending.reason }, environment);
      }
      toast.success(`${pending.flagKey} updated for ${pending.targetLabel} in ${environment.toUpperCase()}.`);
      setReason("");
    } catch (caught) {
      if (caught instanceof FlagStoreError && caught.status === 409) toast.error("Someone changed this since you loaded it. Reloaded; review and try again.");
      else toast.error(caught instanceof Error ? caught.message : "Save failed.");
    } finally {
      setSaving(false);
      setPending(null);
      reload();
    }
  }

  return <div className="space-y-4">
    <Card className="space-y-2 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-mono text-base font-semibold">{definition.key}</h2>
        <Badge variant="secondary">{definition.kind}</Badge>
        {definition.ui_only && <Badge variant="outline">UI only, no server check</Badge>}
        {definition.expires_at && <Badge variant="outline">Expires {definition.expires_at}</Badge>}
      </div>
      <p className="text-sm">{definition.description}</p>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Default</dt><dd>{formatStoredValue(definition.default)}</dd>
        <dt className="text-muted-foreground">Can be set for</dt><dd>{definition.scopes.map(item => SCOPE_LABELS[item]).join(", ")}</dd>
        <dt className="text-muted-foreground">Owner</dt><dd>{definition.owner}{definition.ticket ? ` · ${definition.ticket}` : ""}</dd>
        <dt className="text-muted-foreground">Seen by</dt><dd>{definition.client_exposed.length ? definition.client_exposed.join(", ") : "server only"}</dd>
      </dl>
      <p className="text-xs text-muted-foreground">The most specific value wins: user, then classroom, then organization, then everyone, then the default. Changes reach the backend within 30 seconds and the browser at its next page load.</p>
    </Card>

    <Tabs defaultValue="overrides">
      <TabsList>
        <TabsTrigger value="overrides">Overrides {detail ? `(${overrideCount(detail)})` : ""}</TabsTrigger>
        <TabsTrigger value="history">History</TabsTrigger>
      </TabsList>
      <TabsContent value="overrides" className="space-y-4">
        <Card className="space-y-3 p-4">
          <h3 className="text-sm font-semibold">Set or remove a value</h3>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-xs font-medium">For
              <FieldSelect aria-label="Set the value for" value={scope} disabled={saving} onChange={event => { setScope(event.target.value as FlagScope); setTarget(""); setTargetLabel(""); }}>
                {SCOPE_ORDER.filter(item => definition.scopes.includes(item)).map(item => <option key={item} value={item}>{SCOPE_LABELS[item]}</option>)}
              </FieldSelect>
            </label>
            {scope !== "global" && <div className="flex flex-col gap-1 text-xs font-medium">
              <span>{SCOPE_LABELS[scope]}</span>
              <TargetSearch scope={scope} organizations={organizations} classrooms={classrooms}
                selectedLabel={target ? targetLabel || label(scope, target, current?.name) : ""}
                onSelect={(id, name) => { setTarget(id); setTargetLabel(`${name} (${id})`); }} />
            </div>}
            <label className="flex flex-col gap-1 text-xs font-medium">Value
              {definition.type === "bool" || definition.type === "enum"
                ? <FieldSelect aria-label="Value" value={draft} onChange={event => setDraft(event.target.value)}>
                    <option value="">Choose…</option>
                    {definition.type === "bool"
                      ? <><option value="true">On</option><option value="false">Off</option></>
                      : definition.values?.map(value => <option key={value} value={value}>{value}</option>)}
                  </FieldSelect>
                : <Input aria-label="Value" className="w-32" type={definition.type === "int" ? "number" : "text"} min={definition.min ?? undefined} max={definition.max ?? undefined} value={draft} onChange={event => setDraft(event.target.value)} />}
            </label>
            <label className="flex min-w-64 flex-1 flex-col gap-1 text-xs font-medium">Reason
              <Input aria-label="Reason" placeholder="Why, e.g. pilot for District A (ENGG-1234)" value={reason} maxLength={500} onChange={event => setReason(event.target.value)} />
            </label>
          </div>
          {targetValid && <p className="text-xs text-muted-foreground">Current value here: {formatStoredValue(current?.value ?? null)}</p>}
          {draft && !valueValid && typeof parsed === "string" && <p className="text-xs text-destructive">{parsed}</p>}
          <div className="flex gap-2">
            <Button size="sm" disabled={saving || !targetValid || !valueValid || !reasonValid} onClick={() => review("set")}>Review change</Button>
            <Button size="sm" variant="outline" disabled={saving || !targetValid || !reasonValid || !current} onClick={() => review("clear")}>Remove override</Button>
          </div>
        </Card>

        <Card className="p-4">
          {loading && !detail && <TableSkeleton label="Loading overrides" />}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {detail && overrideCount(detail) === 0 && <p className="text-sm text-muted-foreground">Not set anywhere. Everyone gets the default ({formatStoredValue(definition.default)}).</p>}
          {detail && overrideCount(detail) > 0 && <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground"><tr><th className="py-1 pr-3">For</th><th className="pr-3">Value</th><th className="pr-3">Changed</th><th className="pr-3">Reason</th><th /></tr></thead>
            <tbody>
              {SCOPE_ORDER.flatMap(item => detail.overrides[item] ?? []).map(item => <tr key={`${item.scope}-${item.scope_id}`} className="border-t align-top">
                <td className="py-1 pr-2">{label(item.scope, item.scope_id, item.name)}</td>
                <td className="pr-2 font-medium">{formatStoredValue(item.value)}</td>
                <td className="pr-2 text-xs text-muted-foreground">{item.updated_by?.email}{item.updated_at ? `, ${new Date(item.updated_at).toLocaleString()}` : ""} · v{item.version}</td>
                <td className="pr-2">{item.reason}</td>
                <td><Button size="sm" variant="ghost" onClick={() => edit(item)}>Edit</Button></td>
              </tr>)}
            </tbody>
          </table>}
          {detail?.next_cursor && <p className="mt-2 text-xs text-muted-foreground">Showing the first 50,000 overrides.</p>}
        </Card>
      </TabsContent>
      <TabsContent value="history"><Card className="p-4"><HistoryPanel key={refresh} flagKey={definition.key} targetName={targetName} /></Card></TabsContent>
    </Tabs>

    <ConfirmChangeDialog change={pending} environment={environment} saving={saving} onCancel={() => setPending(null)} onConfirm={confirm} />
  </div>;
}
